import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    TextField,
    Box,
    CircularProgress,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, Download as DownloadIcon } from '@mui/icons-material';
import { usePodklad } from '../../api/JsdhNotifikaceService/service/ZasahMutations';
import { downloadPodkladPdf } from '../../api/JsdhNotifikaceService/service/ZasahService';
import { useToast } from '../toast/useToast';

interface PodkladDialogProps {
    poplachId: number;
    onClose?: () => void;
}

/**
 * Podklad pro zpravu o zasahu (HO-151 Task 3). Text i PDF nesou vetu
 * "pracovni pomucka" primo z BE (`PodkladService.DISCLAIMER`) - komponenta ji
 * NESMI zkratit ani preformulovat, protoze bez ni hrozi, ze velitel podklad
 * odevzda jako oficialni zpravu o zasahu s jinymi nalezitostmi. Proto je
 * upozorneni navic vykreslene samostatne (ne jen jako soucast textboxu), aby
 * bylo videt hned, ne az po precteni celeho textu.
 */
const PodkladDialog: React.FC<PodkladDialogProps> = ({ poplachId, onClose = () => {} }) => {
    const { data, isLoading, isError } = usePodklad(poplachId);
    const { showToast } = useToast();
    const [pdfLoading, setPdfLoading] = useState(false);

    const handleKopirovat = async () => {
        if (!data?.text) return;
        try {
            await navigator.clipboard.writeText(data.text);
            showToast('Podklad zkopírován do schránky.', 'success');
        } catch {
            showToast('Kopírování se nezdařilo.', 'error');
        }
    };

    const handleStahnout = async () => {
        setPdfLoading(true);
        try {
            await downloadPodkladPdf(poplachId);
        } catch {
            showToast('Stažení PDF se nezdařilo.', 'error');
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Podklad pro zprávu o zásahu</DialogTitle>
            <DialogContent>
                {/* Banner NEOPAKUJE vetu z backendu jinymi slovy - ta uz je v textu nize
                    a nesmi se menit. Tady jen zustava na ocich i kdyz uzivatel text
                    odscrolluje, a rika, co ma udelat DAL. */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    Podklad slouží k opsání do oficiální zprávy o zásahu, neodevzdává se místo ní.
                </Alert>

                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                    </Box>
                ) : isError ? (
                    <Alert severity="error">Podklad se nepodařilo načíst.</Alert>
                ) : (
                    <TextField
                        multiline
                        fullWidth
                        minRows={10}
                        maxRows={20}
                        value={data?.text ?? ''}
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleKopirovat} startIcon={<ContentCopyIcon />} disabled={!data?.text}>
                    Kopírovat
                </Button>
                <Button
                    onClick={handleStahnout}
                    startIcon={pdfLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    disabled={pdfLoading}
                >
                    Stáhnout podklad (PDF)
                </Button>
                <Button onClick={onClose}>Zavřít</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PodkladDialog;
